import React, { useEffect, useState, useCallback } from "react";
import { Search, Download, ChevronLeft, ChevronRight } from "lucide-react";
import ScanDismantleUser from "../components/DismantleList/ScanDismantleUsers.js";
import DeleteDismantleButton from "../components/DismantleList/DeleteDismantleButton.js";

// DismantleList React page
// Features:
// - Fetches from GET /dismantle with query params
// - Search (debounced), filters (status, reason, disabled), date range, pagination, sorting
// - Dark mode toggle using Tailwind's `dark` class on root
// - Export visible rows to CSV

export default function DismantleList({ role }) {
  const token = localStorage.getItem("token");

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // filters
  const [q, setQ] = useState("");
  const [disabled, setDisabled] = useState("");
  const [status, setStatus] = useState("");
  const [reason] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // pagination + sorting
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [sortBy] = useState("created_at");
  const [order, setOrder] = useState("DESC");

  // debounce helper
  const useDebounce = (value, delay = 500) => {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
      const t = setTimeout(() => setDebounced(value), delay);
      return () => clearTimeout(t);
    }, [value, delay]);
    return debounced;
  };

  const qDebounced = useDebounce(q, 450);

  // build query string
  const buildQuery = () => {
    const params = new URLSearchParams();
    if (qDebounced) params.append("q", qDebounced);
    if (disabled) params.append("disabled", disabled);
    if (status) params.append("status", status);
    if (reason) params.append("reason", reason);
    if (dateFrom) params.append("from", dateFrom);
    if (dateTo) params.append("to", dateTo);
    if (limit) params.append("limit", limit);
    if (page) params.append("page", page);
    if (sortBy) params.append("sortBy", sortBy);
    if (order) params.append("order", order);
    return params.toString();
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = buildQuery();
      const res = await fetch(
        `https://api.wifikamu.my.id/dismantle-users?${qs}`,
        {
          headers: token ? { Authorization: token } : {},
        }
      );
      if (!res.ok) throw new Error(`Server responded ${res.status}`);
      const json = await res.json();
      if (!json.success) throw new Error(json.message || "Unknown error");
      setData(json.data || []);
      setTotal(json.pagination?.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [
    qDebounced,
    disabled,
    status,
    reason,
    dateFrom,
    dateTo,
    limit,
    page,
    sortBy,
    order,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDate = (s) => {
    if (!s) return "-";
    try {
      const d = new Date(s);
      return d.toLocaleString();
    } catch (e) {
      return s;
    }
  };

  const exportCSV = () => {
    if (!data || data.length === 0) return;
    const keys = Object.keys(data[0]);
    const lines = [keys.join(",")];
    data.forEach((row) => {
      const vals = keys.map((k) => {
        let v = row[k];
        if (v === null || v === undefined) return "";
        if (typeof v === "object") v = JSON.stringify(v);
        return String(v).replace(/\"/g, '""');
      });
      lines.push(vals.join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dismantle_export_page${page}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="min-h-screen p-6 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <div className="max-w-6xl mx-auto">
        <header className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-semibold">Dismantle List</h1>
          {role === "billing" && (
            <div className="flex items-center gap-3">
              <ScanDismantleUser token={token} onSuccess={fetchData} />
              <button
                onClick={exportCSV}
                className="p-2 rounded-md bg-emerald-600 text-white hover:opacity-90 flex items-center gap-2"
              >
                <Download size={16} /> Export CSV
              </button>
            </div>
          )}
        </header>

        <section className="bg-white dark:bg-gray-800 p-4 rounded-md shadow-sm mb-4">
          <div className="flex gap-3 items-center">
            <div className="flex items-center gap-2 flex-1">
              <Search size={16} />
              <input
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setPage(1);
                }}
                placeholder="Search name, cust_id, note, comment..."
                className="w-full bg-transparent outline-none"
              />
            </div>

            <select
              value={disabled}
              onChange={(e) => {
                setDisabled(e.target.value);
                setPage(1);
              }}
              className="px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700"
            >
              <option value="">All</option>
              <option value="true">Disabled = true</option>
              <option value="false">Disabled = false</option>
            </select>

            <select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
              className="px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700"
            >
              <option value="">All Status</option>
              <option value="Added">Added</option>
              <option value="Updated">Updated</option>
              <option value="Failed">Failed</option>
            </select>

            <select
              value={order}
              onChange={(e) => {
                setOrder(e.target.value);
              }}
              className="px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700"
            >
              <option value="DESC">Newest</option>
              <option value="ASC">Oldest</option>
            </select>
          </div>

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setPage(1);
              }}
              className="px-2 py-1 rounded-md bg-gray-50 dark:bg-gray-700"
            />
            <input
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setPage(1);
              }}
              className="px-2 py-1 rounded-md bg-gray-50 dark:bg-gray-700"
            />

            <div className="flex items-center gap-2">
              <label>Per page</label>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(parseInt(e.target.value));
                  setPage(1);
                }}
                className="px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        </section>

        <section className="bg-white dark:bg-gray-800 p-4 rounded-md shadow-sm">
          {loading ? (
            <div className="py-12 text-center">Loading...</div>
          ) : error ? (
            <div className="py-6 text-red-500">{error}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full table-auto border-collapse">
                <thead>
                  <tr className="text-sm text-left border-b">
                    <th className="py-2">#</th>
                    <th className="py-2">Name</th>
                    <th className="py-2">Cust ID</th>
                    <th className="py-2">Disabled</th>
                    <th className="py-2">Status</th>
                    <th className="py-2">Note</th>
                    <th className="py-2">Reason</th>
                    <th className="py-2">Last Update</th>
                    {role === "billing" && <th className="py-2">.</th>}
                  </tr>
                </thead>
                <tbody>
                  {data.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="py-6 text-center">
                        No data
                      </td>
                    </tr>
                  ) : (
                    data.map((row, i) => (
                      <tr
                        key={row.id}
                        className="text-sm border-b last:border-b-0"
                      >
                        <td className="py-2">{(page - 1) * limit + i + 1}</td>
                        <td className="py-2 font-medium">{row.name}</td>
                        <td className="py-2">{row.cust_id}</td>
                        <td className="py-2">
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs ${
                              row.disabled === "true"
                                ? "bg-red-100 text-red-800 dark:bg-red-800/30"
                                : "bg-green-100 text-green-800 dark:bg-green-800/30"
                            }`}
                          >
                            {row.disabled}
                          </span>
                        </td>
                        <td className="py-2">{row.status || "-"}</td>
                        <td className="py-2">{row.note || "-"}</td>
                        <td className="py-2">{row.reason || "-"}</td>
                        <td className="py-2">{formatDate(row.updated_at)}</td>
                        {role === "billing" && (
                          <td className="py-2">
                            <DeleteDismantleButton
                              id={row.id}
                              token={token}
                              onSuccess={fetchData}
                            />
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          <footer className="mt-4 flex items-center justify-between">
            <div className="text-sm">
              Total: {total} — Page {page} / {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                className="p-2 rounded-md bg-gray-100 dark:bg-gray-700"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={() =>
                  setPage((prev) => Math.min(totalPages, prev + 1))
                }
                className="p-2 rounded-md bg-gray-100 dark:bg-gray-700"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </footer>
        </section>
      </div>
    </div>
  );
}
